// ─────────────────────────────────────────────
// Oike — from an accepted proposal to the process of each service it sold.
// "Preparar inicio del servicio" reads the quote (Diagnostic Intake) and, for the
// line Ale chooses, creates or links the process in the module that already
// exists: a diagnostic (Diagnostic), the System Design of a diagnostic, or a
// coaching process (Acompañamiento, from a program). Services without a route
// in Oike (Enablement, Advisory…) are only pointed out, with the minimal link.
// Rules (docs/servicios/inicio.md):
// - Accepted (Quote Status = Won) is not paid nor started: preparing never
//   writes a payment nor Service Started At.
// - One line, one process: a second request finds the first (double click,
//   retry), and a process already linked to another line is never re-linked.
// - The origin is kept on the process (Service Origin / Service Origins: quote
//   id, line, when, by); the request and the opportunity are reached through
//   the quote, never copied. People are reused by their identity card only.
// Shared by the panel (browser), diagnostic-admin, coaching-admin and the tests.
// ─────────────────────────────────────────────
import { PRICING_CONFIG } from './pricing.js';

/** Where a service can start. `module` is the screen that owns it. */
export const DESTINATIONS = {
  diagnostic: { label: 'Diagnostic', module: 'diagnostic', action: 'Crear o vincular un diagnóstico de la empresa' },
  systemDesign: { label: 'System Design', module: 'diagnostic', action: 'Vincular el System Design de un diagnóstico existente' },
  coaching: { label: 'Acompañamiento', module: 'coaching', action: 'Crear o vincular un proceso desde un programa' },
  none: { label: 'Todavía sin recorrido en Oike', module: null, action: '' },
};

/**
 * Services sold without their own route in Oike. Nothing is created for them:
 * the panel says so and proposes the minimal link, which Ale may choose by hand.
 */
export const MISSING_ROUTES = {
  enablement: {
    label: 'Oike Enablement',
    missing: 'No tiene un módulo propio: workshops, role plays y seguimiento de adopción no tienen dónde registrarse.',
    minimal: 'Conexión mínima: un proceso de Acompañamiento de equipo, con un programa que armes para la formación. La especialidad de ventas se agrega solo si la elegís.',
    canUse: ['coaching'],
  },
  advisory: {
    label: 'Oike Advisory',
    missing: 'No tiene un módulo propio: el retainer mensual no tiene sesiones ni seguimiento propios.',
    minimal: 'Conexión mínima: un proceso de Acompañamiento individual con el founder o líder comercial, con un programa que armes para el retainer. La especialidad de negocios se agrega solo si la elegís.',
    canUse: ['coaching'],
  },
  other: {
    label: 'Otro servicio',
    missing: 'Esta línea no corresponde a un servicio con recorrido en Oike.',
    minimal: 'Si es un acompañamiento, elegí "Acompañamiento" y el programa. Si no, se gestiona fuera de Oike por ahora.',
    canUse: ['coaching'],
  },
};

const COACHING_RE = /acompa[ñn]amiento|coaching|mentor[ií]a/i;

/** A stable key for one line of a quote: its position and its service, so an edited line is not mistaken for another. */
export function lineKeyOf(index, service) {
  const slug = String(service || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
  return `${index}:${slug || 'linea'}`;
}

/** The pricing key of a line ('diagnostic', 'systemDesign', 'enablement', 'advisory'), 'coaching' for an Acompañamiento line, or 'other'. */
export function serviceKeyOf(label) {
  const l = String(label || '').trim();
  const hit = Object.entries(PRICING_CONFIG.services).find(([, s]) => s.label.toLowerCase() === l.toLowerCase());
  if (hit) return hit[0];
  if (COACHING_RE.test(l)) return 'coaching';
  return 'other';
}

/** Where a line starts by default: only where the module exists; otherwise nowhere until Ale chooses. */
export function suggestedDestination(serviceKey) {
  if (serviceKey === 'diagnostic' || serviceKey === 'systemDesign' || serviceKey === 'coaching') return serviceKey;
  return 'none';
}

/** The destinations Ale may choose for a line (the suggested one first). */
export function destinationsFor(serviceKey) {
  const suggested = suggestedDestination(serviceKey);
  const extra = MISSING_ROUTES[serviceKey]?.canUse || [];
  return [...new Set([suggested, ...extra, 'none'])];
}

/** The lines of a quote, each with its key, service and suggested destination. Empty lines are left out. */
export function quoteLinesOf(fields = {}) {
  let lines = [];
  try { lines = JSON.parse(fields['Quote Lines JSON'] || '[]'); } catch { lines = []; }
  if (!Array.isArray(lines)) lines = [];
  return lines.map((l, index) => ({ l, index })).filter(({ l }) => String(l?.service || '').trim()).map(({ l, index }) => {
    const service = String(l.service).trim();
    const serviceKey = serviceKeyOf(service);
    return {
      lineKey: lineKeyOf(index, service), index, service, scope: String(l.scope || ''),
      onceFee: l.onceFee ?? '', monthlyFee: l.monthlyFee ?? '',
      serviceKey, suggested: suggestedDestination(serviceKey), destinations: destinationsFor(serviceKey),
      missingRoute: MISSING_ROUTES[serviceKey] || null,
    };
  });
}

const statusName = (v) => (v && typeof v === 'object' ? v.name : v) || '';

/** Only an accepted proposal (Quote Status = Won) can prepare the start of a service. */
export function isAcceptedQuote(fields = {}) {
  return statusName(fields['Quote Status']) === 'Won';
}

// ── The origin kept on the process ──

/** One origin: which quote and line a process comes from, how it was prepared, when and by whom. */
export function originEntry({ quoteId, quoteName = '', lineKey, service, destination, mode, by = '', at = new Date().toISOString() }) {
  if (!/^rec[A-Za-z0-9]{3,30}$/.test(String(quoteId || ''))) throw new Error('invalid quote');
  if (!lineKey) throw new Error('missing line');
  if (!DESTINATIONS[destination] || destination === 'none') throw new Error('invalid destination');
  if (!['created', 'linked'].includes(mode)) throw new Error('invalid mode');
  return { quoteId, quoteName: String(quoteName).slice(0, 200), lineKey: String(lineKey).slice(0, 60), service: String(service || '').slice(0, 120), destination, mode, at, by: String(by).slice(0, 200) };
}

/** The origins of a record: Service Origins (a JSON list, diagnostics) or Service Origin (one JSON object, coaching processes). */
export function originsOf(raw) {
  if (!raw) return [];
  let v;
  try { v = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return []; }
  const list = Array.isArray(v) ? v : [v];
  return list.filter(o => o && typeof o === 'object' && o.quoteId && o.lineKey);
}

export const sameLine = (o, quoteId, lineKey) => o.quoteId === quoteId && o.lineKey === lineKey;

/**
 * The record that holds one line, among records with their origins ({ id, createdAt, origins }).
 * Two requests at once (double click, retry) can both create: the earliest one wins
 * and the others are duplicates to undo.
 */
export function holderOfLine(records, quoteId, lineKey, destination) {
  const holders = records.filter(r => (r.origins || []).some(o => sameLine(o, quoteId, lineKey) && (!destination || o.destination === destination)))
    .sort((a, b) => String(a.createdAt || '').localeCompare(String(b.createdAt || '')) || String(a.id).localeCompare(String(b.id)));
  return { holder: holders[0] || null, duplicates: holders.slice(1) };
}

/**
 * What the review step says before anything is written: what will be created or
 * linked, and what it does NOT do. `plan`: { destination, mode, targetName, accountName,
 * programName, people: [{ name, card: 'existing'|'new'|'from-contact' }], quoteName, service }.
 */
export function reviewOf(plan) {
  const out = [];
  const who = (plan.people || []).map(p => `${p.name}${p.card === 'existing' ? ' (ficha existente)' : ' (ficha nueva desde el contacto)'}`);
  if (plan.destination === 'diagnostic') {
    out.push(plan.mode === 'linked'
      ? `Se vincula el diagnóstico existente «${plan.targetName}» de ${plan.accountName} a esta línea.`
      : `Se crea el diagnóstico «${plan.targetName}» para ${plan.accountName}, en preparación (sin formularios abiertos).`);
    if (who.length) out.push(`Participantes: ${who.join(', ')}. Cada uno con su propio link; nada de lo comercial se copia.`);
  }
  if (plan.destination === 'systemDesign') out.push(`Se vincula esta línea al System Design del diagnóstico «${plan.targetName}». No se abre ni se cambia nada del diagnóstico.`);
  if (plan.destination === 'coaching') {
    out.push(plan.mode === 'linked'
      ? `Se vincula el proceso existente «${plan.targetName}» a esta línea. Sus personas, permisos y sesiones no cambian.`
      : `Se crea el proceso «${plan.targetName}» con el programa «${plan.programName}», en estado invitado.`);
    if (plan.mode !== 'linked' && who.length) out.push(`Personas: ${who.join(', ')}. Cada una recibe su propio link y lo ve en su espacio.`);
  }
  out.push(`Origen guardado en el proceso: propuesta «${plan.quoteName}», línea «${plan.service}». La solicitud y la oportunidad se siguen leyendo desde la propuesta.`);
  out.push('No marca que el servicio empezó ni registra pagos: eso se registra aparte en el pipeline.');
  out.push('No copia notas comerciales: de cada persona se usa solo su ficha (nombre, email, zona horaria y contacto vinculado).');
  return out;
}

/** Default names, editable before creating. */
export const defaultDiagnosticName = (accountName) => `${accountName || 'Empresa'} — Diagnostic`;
export const defaultProcessName = ({ modality, personName, programName, accountName }) =>
  (modality === 'individual' ? `${personName || 'Persona'} · ${String(programName || '').replace(/\s*\(propuesta editable\)\s*$/i, '')}` : `${accountName ? `${accountName} · ` : ''}${String(programName || '').replace(/\s*\(propuesta editable\)\s*$/i, '')}`).slice(0, 200);

/**
 * The accepted quote as both functions read it: id, name, account, the contacts
 * it names, the diagnostic it came from (a follow-up quote) and its lines.
 * Throws when it is not accepted: only a won proposal prepares a service.
 */
export function acceptedQuoteFrom(record) {
  const f = record?.fields || {};
  if (!isAcceptedQuote(f)) throw new Error('quote not accepted');
  const ids = (v) => (Array.isArray(v) ? v.filter(x => typeof x === 'string') : []);
  return {
    id: record.id, name: String(f['Name'] || 'Propuesta'),
    accountId: ids(f['Account'])[0] || '', stakeholderIds: ids(f['Stakeholder']),
    sourceDiagnosticId: ids(f['Source Diagnostic'])[0] || '',
    lines: quoteLinesOf(f),
  };
}

/** One line of the quote by its key, or an error (the quote changed since the panel was opened). */
export function lineOf(quote, lineKey) {
  const line = quote.lines.find(l => l.lineKey === lineKey);
  if (!line) throw new Error('line not found');
  return line;
}

/** The forms of a new Diagnostic participant by role, as the Diagnostic tab sets them (AccountDiagnosticTab). */
export const DIAGNOSTIC_DEFAULT_FORMS = { founder_seller: ['self', 'inventory'], manager: ['manager'] };
export const defaultFormsForRole = (role) => DIAGNOSTIC_DEFAULT_FORMS[role] || ['self'];
